/**
 *
 *  PasswordConfirmForm
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 02/03/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useState, useEffect, useCallback } from "react";
import { useAppState } from "../../state";
import { useSignUpState } from "../../state/SignUpState";

// Components
import Button from "../Button/Button";
import PasswordInput from "../PasswordInput/PasswordInput";

// Icons
import CheckNiceIcon from "../../icons/CheckNiceIcon";
import { SendCodeAgainButton } from "../SignUp/CodeVerification/CodeVerification";

export default function PasswordResetEmail() {
  const [canSend, setCanSend] = useState(false);
  const [password, setPassword] = useState("");
  const [passwordConfirm, setPasswordConfirm] = useState("");
  const [hasError, setHasError] = useState(false);

  const {
    sendResetCode,
    resetPassword,
    migrationCode,
    // resetCode,
    sendRegistrationLink,
    error,
    isFetching,
  } = useAppState();
  const {
    userPhoneCode: phoneCode,
    userPhoneNumber: phoneNumber,
    userEmail,
    setAuthState,
  } = useSignUpState();

  const newPasswordRef = React.useRef();
  const newPasswordConfirmRef = React.useRef();

  const handlePasswordChange = useCallback((e) => {
    const value = e.target.value;

    setHasError(false);
    setPassword(value);
  }, []);

  const handlePasswordConfirmChange = useCallback((e) => {
    const value = e.target.value;

    setHasError(false);
    setPasswordConfirm(value);
  }, []);

  useEffect(() => {
    if (!migrationCode && userEmail) {
      sendRegistrationLink(userEmail).catch(() => {
        setHasError(true);
      });
    }
  }, [
    // eslint-disable-line react-hooks/exhaustive-deps
    userEmail,
    migrationCode,
  ]);

  useEffect(() => {
    if (password && passwordConfirm && password === passwordConfirm) {
      setCanSend(true);
    } else {
      setCanSend(false);
    }
  }, [password, passwordConfirm]);

  const sendEmailAgain = useCallback(() => {
    if (userEmail) {
      setHasError(false);

      sendRegistrationLink(userEmail).catch(() => {
        setHasError(true);
      });
    }
  }, [userEmail]);

  const handleSubmit = useCallback(
    (e) => {
      e.preventDefault();

      if (password.length < 5) {
        setHasError(true);

        return;
      }

      setAuthState("setPhoneNumber");
    },
    [password]
  );

  return (
    <>
      {migrationCode ? (
        <>
          <div className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
            Create Your New Password
          </div>
          <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
            Please use uppercase and lowercase letters, numbers and symbols.
          </p>
          <form onSubmit={handleSubmit}>
            <div className="">
              <div className="tbk-relative tbk-mb-3">
                <div className="tbk-text-small tbk-mb-0.5 tbk-text-black-grey">
                  New password:
                </div>
                <PasswordInput
                  ref={newPasswordRef}
                  autoComplete="new-password"
                  placeholder="Your password"
                  minLength={5}
                  maxLength={30}
                  pattern="\s*(\S\s*){5,30}"
                  password={password}
                  onChange={handlePasswordChange}
                  // onInput={handlePasswordChange}
                />
              </div>
              <div className="tbk-relative tbk-mb-3">
                <div className="tbk-text-small tbk-mb-0.5 tbk-text-black-grey">
                  Confirm new password:
                </div>
                <PasswordInput
                  ref={newPasswordConfirmRef}
                  autoComplete="new-password"
                  placeholder="Confirm password"
                  minLength={5}
                  maxLength={30}
                  pattern="\s*(\S\s*){5,30}"
                  password={passwordConfirm}
                  onChange={handlePasswordConfirmChange}
                  // onInput={handlePasswordConfirmChange}
                />
              </div>
            </div>
            {hasError ? (
              <div className="tbk-text-main tbk--mt-2 tbk-mb-2 tbk-text-center tbk-text-coral">
                {error ? error.message : "Unknown error has occurred"}
              </div>
            ) : null}
            <Button
              type="submit"
              className="tbk-w-full"
              disabled={!canSend || isFetching}
              showLoader={isFetching}
              endIcon={<CheckNiceIcon />}
            >
              Save
            </Button>
          </form>
        </>
      ) : (
        <>
          <div className="tbk-text-title-bold tbk-mb-1 tbk-text-primary">
            Check Your Email
          </div>
          <p className="tbk-text-main tbk-mb-1 tbk-text-primary">
            We sent an email to{" "}
            <span className="tbk-text-coral">{userEmail}</span>.
          </p>
          <p className="tbk-text-main tbk-mb-4 tbk-text-primary">
            Check the email in order to reset your password.
          </p>
          {hasError ? (
            <div className="tbk-text-main tbk--mt-2 tbk-mb-2 tbk-text-center tbk-text-coral">
              {error ? error.message : "Unknown error has occurred"}
            </div>
          ) : null}
          <div className="tbk-mb-5">
            <SendCodeAgainButton label="Send again" onClick={sendEmailAgain} />
          </div>
        </>
      )}
    </>
  );
}
